/**
 * Create Debit Note Page
 *
 * Dedicated page for creating debit notes to add charges to an existing invoice.
 */

import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Plus, Trash2, PlusCircle, ArrowLeft, Loader2 } from 'lucide-react'

import api from '@/lib/api'
import { useCreateDebitNote } from '@/hooks/useBills'
import { formatCurrency } from '@/lib/utils'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/toaster'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import {
  BillDetail,
  ReferenceCode,
  REFERENCE_CODE_LABELS,
  DEBIT_NOTE_REFERENCE_CODES,
  DebitNoteLineRequest,
} from '@/types'

interface ChargeLine {
  id: string
  description: string
  quantity: number
  unitPrice: number
  cabysCode: string
}

const generateId = () => Math.random().toString(36).substring(2, 11)

export function CreateDebitNotePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const createDebitNoteMutation = useCreateDebitNote()

  // Fetch bill data
  const { data: bill, isLoading } = useQuery({
    queryKey: ['bill', id],
    queryFn: async () => {
      const response = await api.get(`/bills/${id}`)
      return response.data as BillDetail
    },
    enabled: !!id,
  })

  // Form state
  const [referenceCode, setReferenceCode] = useState<ReferenceCode | ''>('')
  const [reason, setReason] = useState('')
  const [charges, setCharges] = useState<ChargeLine[]>([])

  // Add new charge line
  const handleAddCharge = () => {
    setCharges([
      ...charges,
      {
        id: generateId(),
        description: '',
        quantity: 1,
        unitPrice: 0,
        cabysCode: '',
      },
    ])
  }

  // Remove charge line
  const handleRemoveCharge = (id: string) => {
    setCharges(charges.filter((c) => c.id !== id))
  }

  // Update charge line
  const handleUpdateCharge = (id: string, field: keyof ChargeLine, value: string | number) => {
    setCharges(
      charges.map((c) =>
        c.id === id ? { ...c, [field]: value } : c
      )
    )
  }

  // Calculate totals
  const calculateTotal = () => {
    let subtotal = 0
    charges.forEach((charge) => {
      subtotal += charge.quantity * charge.unitPrice
    })
    const tax = subtotal * 0.13
    return { subtotal, tax, total: subtotal + tax }
  }

  const totals = calculateTotal()

  // Validate form
  const isValid = () => {
    if (!referenceCode) return false
    if (charges.length === 0) return false

    return charges.every(
      (c) =>
        c.description.trim() !== '' &&
        c.quantity > 0 &&
        c.unitPrice > 0 &&
        c.cabysCode.length === 13
    )
  }

  // Submit handler
  const handleSubmit = async () => {
    if (!isValid() || !bill) return

    const lines: DebitNoteLineRequest[] = charges.map((c) => ({
      description: c.description,
      quantity: c.quantity,
      unit_price: c.unitPrice,
      cabys_code: c.cabysCode,
    }))

    try {
      const result = await createDebitNoteMutation.mutateAsync({
        billId: bill.id,
        data: {
          reference_code: referenceCode,
          reason: reason || undefined,
          lines,
        },
      })

      if (result.success) {
        toast({ title: t('invoicing.debitNoteSuccess') })
        navigate(`/bills/${bill.id}`)
      } else {
        toast({
          title: t('common.error'),
          description: result.message || t('invoicing.debitNoteError'),
          variant: 'destructive',
        })
      }
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || t('invoicing.debitNoteError'),
        variant: 'destructive',
      })
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!bill) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-muted-foreground">{t('bills.notFound')}</p>
        <Button variant="outline" onClick={() => navigate('/bills')} className="mt-4">
          {t('common.back')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-24">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/bills/${bill.id}`)}
          className="shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <Plus className="h-5 w-5 text-blue-500" />
            {t('invoicing.debitNoteTitle')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('invoicing.debitNoteDescription')}
          </p>
        </div>
      </div>

      {/* Reference Code */}
      <Card>
        <CardHeader>
          <CardTitle>{t('invoicing.selectReferenceCode')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>{t('invoicing.selectReferenceCode')} *</Label>
            <Select
              value={referenceCode}
              onValueChange={(value) => setReferenceCode(value as ReferenceCode)}
            >
              <SelectTrigger className="h-12">
                <SelectValue placeholder={t('invoicing.selectReferenceCode')} />
              </SelectTrigger>
              <SelectContent>
                {DEBIT_NOTE_REFERENCE_CODES.map((code) => (
                  <SelectItem key={code} value={code}>
                    <span className="font-mono mr-2">{code}</span>
                    {REFERENCE_CODE_LABELS[code]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t('invoicing.referenceReason')}</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('invoicing.referenceReasonPlaceholder')}
              rows={2}
              className="resize-none"
            />
          </div>

          {/* Original document info */}
          <div className="p-4 bg-muted/50 rounded-lg space-y-1">
            <p className="text-xs text-muted-foreground">{t('invoicing.originalDocument')}</p>
            <p className="font-medium text-lg">{bill.bill_number || `#${bill.id}`}</p>
            <p className="text-muted-foreground">
              Total original: {formatCurrency(bill.total)}
            </p>
            {bill.activity_code && (
              <p className="text-xs text-muted-foreground">
                {t('activities.economicActivity')}: {bill.activity_code}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Charges */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-lg">{t('invoicing.additionalCharges')}</CardTitle>
              <CardDescription>{t('invoicing.additionalChargesHint')}</CardDescription>
            </div>
            <Button type="button" variant="outline" onClick={handleAddCharge}>
              <PlusCircle className="h-4 w-4 mr-2" />
              {t('invoicing.addCharge')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {charges.length === 0 ? (
            <div className="p-8 text-center border border-dashed rounded-lg">
              <PlusCircle className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground">{t('invoicing.noCharges')}</p>
              <p className="text-sm text-muted-foreground">{t('invoicing.addChargesFirst')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {charges.map((charge, index) => (
                <div
                  key={charge.id}
                  className="p-4 border rounded-lg space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Cargo #{index + 1}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-500"
                      onClick={() => handleRemoveCharge(charge.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('invoicing.chargeDescriptionLabel')} *</Label>
                    <Input
                      placeholder={t('invoicing.chargeDescription')}
                      value={charge.description}
                      onChange={(e) =>
                        handleUpdateCharge(charge.id, 'description', e.target.value)
                      }
                      className="h-12"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('invoicing.quantity')} *</Label>
                      <Input
                        type="number"
                        min={1}
                        value={charge.quantity}
                        onChange={(e) =>
                          handleUpdateCharge(charge.id, 'quantity', parseInt(e.target.value) || 1)
                        }
                        className="h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('invoicing.price')} *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        value={charge.unitPrice}
                        onChange={(e) =>
                          handleUpdateCharge(charge.id, 'unitPrice', parseFloat(e.target.value) || 0)
                        }
                        className="h-12"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>CABYS *</Label>
                    <Input
                      placeholder={t('invoicing.cabysPlaceholder')}
                      maxLength={13}
                      value={charge.cabysCode}
                      onChange={(e) =>
                        handleUpdateCharge(charge.id, 'cabysCode', e.target.value.replace(/\D/g, ''))
                      }
                      className={`h-12 font-mono ${
                        charge.cabysCode && charge.cabysCode.length !== 13
                          ? 'border-red-500'
                          : ''
                      }`}
                    />
                    <p className="text-xs text-muted-foreground">
                      {charge.cabysCode.length}/13 dígitos
                    </p>
                  </div>

                  <div className="text-right text-sm font-medium">
                    Subtotal: {formatCurrency(charge.quantity * charge.unitPrice)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Totals */}
      {charges.length > 0 && (
        <Card>
          <CardContent className="p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span>{t('bills.subtotal')}</span>
              <span>{formatCurrency(totals.subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>{t('bills.tax')} (13%)</span>
              <span>{formatCurrency(totals.tax)}</span>
            </div>
            <div className="flex justify-between font-bold text-lg border-t pt-2">
              <span>{t('invoicing.noteTotal')}</span>
              <span className="text-blue-600">+{formatCurrency(totals.total)}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Warning */}
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          {t('invoicing.debitNoteWarning')}
        </AlertDescription>
      </Alert>

      {/* Fixed Footer Actions */}
      <div className="fixed bottom-16 left-0 right-0 p-4 bg-background border-t z-50 md:relative md:bottom-0 md:border-0 md:p-0 md:bg-transparent md:z-auto">
        <div className="flex gap-2 max-w-screen-lg mx-auto">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/bills/${bill.id}`)}
            className="flex-1 h-12"
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!isValid() || createDebitNoteMutation.isPending}
            className="flex-1 h-12"
          >
            {createDebitNoteMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            {createDebitNoteMutation.isPending
              ? t('invoicing.creating')
              : t('invoicing.confirmCreate')}
          </Button>
        </div>
      </div>
    </div>
  )
}
